import React, { useEffect, useState, useRef } from "react";
import { Drawer, Row, Col, Typography, Form, Button, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import AppointmentFormList from "./AppointmentFormList";
import AppointmentPatientSummary from "./AppointmentPatientSummary";
import {
  getPatientDetailsApi,
  updateAppointmentApi,
} from "../../../services/receptionist.services";
import btnStyles from "../../../styles/Custom/Button.module.css";
import { capitalizeFirstLetter } from "../../../helpers/auth/helper";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import "../receptionistMobileForms.css";
import styles from "./EditAppointmentModal.module.css";

const EditAppointmentModal = ({ open, setOpen, editData, refresh }) => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEndSlot, setSelectedEndSlot] = useState(null);

  useEffect(() => {
    if (editData?.id) {
      form?.setFieldsValue({
        ...editData,
        appointment_date: dayjs(editData?.appointment_date),
        name: `${capitalizeFirstLetter(
          editData?.doctor_name.split(" ")[0]
        )} ${capitalizeFirstLetter(editData?.doctor_name.split(" ")[1])}`,
        patient_mobile: editData?.patient_mobile,
      });
      setFacilityId(editData?.facility);
      setSelectedPractitioner(editData?.doctor);
      setSelectedId(editData?.patient);
      setSelectedSlot(
        editData?.appointment_time.split(":").slice(0, 2).join(":")
      );
      setSelectedEndSlot(editData?.appointment_end_time);
    }
  }, [editData?.id]);

  useEffect(() => {
    if (editData?.patient) {
      getPatientDetailsApi(editData?.patient)
        .then((res) => setPatientDetails(res?.data))
        .catch((err) => console.log(err));
    }
  }, [editData?.patient]);

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const { dummyMobile, name, appointment_date, ...formData } = values;

      const formattedAppointmentDate = appointment_date
        ? dayjs(appointment_date).format("YYYY-MM-DD")
        : editData?.appointment_date;

      const updatedFormData = {
        ...formData,
        appointment_date: formattedAppointmentDate,
        doctor: selectedPractitioner,
        appointment_time: selectedSlot,
        appointment_end_time: selectedEndSlot,
        facility: facilityId,
        patient: selectedId,
        patient_first_name: editData?.patient_first_name,
        patient_last_name: editData?.patient_last_name,
        patient_age: editData?.patient_age,
        patient_gender: editData?.patient_gender,
        patient_mobile: editData?.patient_mobile,
        appointment_id: editData?.id,
      };

      const response = await updateAppointmentApi(updatedFormData);
      message.success(response?.data?.message);
      setOpen(false);
      refresh();
    } catch (error) {
      message.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  const onValuesChange = (changedValues, values) => {
    setFormValues({ ...values, ...changedValues });
  };

  return (
    <>
      <Drawer
        width={isMobile ? "100%" : "70%"}
        rootClassName={
          isMobile
            ? "reception-drawer-fullscreen edit-appointment-drawer-mobile"
            : undefined
        }
        open={open}
        onClose={() => setOpen(false)}
        closeIcon={null}
        styles={
          isMobile
            ? {
                header: {
                  padding: "12px 16px 10px",
                  borderBottom: "none",
                },
                body: { padding: "12px 16px 16px" },
                footer: { padding: "12px 16px 16px", borderTop: "1px solid #eef2f7" },
              }
            : undefined
        }
        title={
          isMobile ? (
            <div className={styles.editAppointmentMobileHeader}>
              <div className="reception-drawer-mobile-title">
                <Typography
                  className="reception-drawer-mobile-title-text"
                  style={{ fontWeight: 400, fontSize: "18px" }}
                >
                  Edit Appointment
                </Typography>
                <CloseOutlined onClick={() => setOpen(false)} />
              </div>
            </div>
          ) : (
            <Row>
              <Col span={20} className="flex-start">
                <Typography
                  style={{
                    marginLeft: "12px",
                    fontWeight: "400",
                    fontSize: "20px",
                  }}
                >
                  Edit Appointment
                </Typography>
              </Col>
              <Col span={4} className="flex-end">
                <CloseOutlined onClick={() => setOpen(false)} />
              </Col>
            </Row>
          )
        }
        footer={
          isMobile ? (
            <div className="reception-drawer-footer-row">
              <Button
                loading={loading}
                htmlType="submit"
                type="primary"
                className={btnStyles.mdBtn}
                onClick={() => formRef.current.submit()}
              >
                Save
              </Button>
            </div>
          ) : (
            <div className="flex-end" style={{ gap: "24px" }}>
              <Button
                loading={loading}
                htmlType="submit"
                type="primary"
                className={btnStyles.mdBtn}
                onClick={() => formRef.current.submit()}
              >
                Save
              </Button>
            </div>
          )
        }
      >
        <AppointmentPatientSummary
          editData={editData}
          patientDetails={patientDetails}
        />
        <Form
          layout="vertical"
          form={form}
          ref={formRef}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
        >
          <AppointmentFormList
            facilityId={facilityId}
            setFacilityId={setFacilityId}
            formValues={formValues}
            selectedPractitioner={selectedPractitioner}
            setSelectedPractitioner={setSelectedPractitioner}
            selectedSlot={selectedSlot}
            setSelectedSlot={setSelectedSlot}
            selectedEndSlot={selectedEndSlot}
            setSelectedEndSlot={setSelectedEndSlot}
            selectedId={selectedId}
            editData={editData}
            form={form}
          />
        </Form>
      </Drawer>
    </>
  );
};

export default EditAppointmentModal;
